import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { DashboardLayout } from '@/components/layout'
import { PageHeader, StatTile } from '@/lib/atsUi'
import { atsNav } from '@/lib/atsNav'
import { currentTenant, currentUser, commissions, vendors } from '@/lib/mocks'
import { Badge } from '@/components/ui/badge'
import { DataList } from '@/components/ui/data-list'
import { Tabs } from '@/components/ui/tabs'

export const Route = createFileRoute('/commissions')({
  component: CommissionsPage,
})

type Tab = 'ledger' | 'partners'

function CommissionsPage() {
  const [tab, setTab] = useState<Tab>('ledger')
  const paidCommissions = commissions.filter((c) => c.status === 'paid')
  const pendingCommissions = commissions.filter((c) => c.status === 'pending')

  return (
    <DashboardLayout title="Commissions" navSections={atsNav} tenant={currentTenant} user={currentUser}>
      <PageHeader title="Commissions" subtitle="What each sourcing partner has earned on your placements." />

      {/* Stats */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 mb-8">
        <StatTile label="Total payable" value="₹4,37,250" />
        <StatTile label="Paid" value={`${paidCommissions.length} · ₹64,050`} />
        <StatTile label="Pending approval" value={`${pendingCommissions.length} · ₹1,13,200`} />
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <Tabs
          items={[
            { value: 'ledger', label: 'Commission ledger', count: commissions.length },
            { value: 'partners', label: 'Sourcing partners', count: vendors.length },
          ]}
          value={tab}
          onChange={(v) => setTab(v as Tab)}
        />
      </div>

      {/* Commission ledger tab */}
      {tab === 'ledger' && (
        <DataList
          items={commissions}
          getKey={(c) => c.id}
          pageSize={5}
          empty="No commissions yet."
          renderRow={(commission) => (
            <div className="flex flex-col gap-2 px-5 py-4 sm:flex-row sm:items-center sm:gap-4 hover:bg-muted/40 transition-colors">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium leading-snug">{commission.candidate}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{commission.client}</p>
              </div>
              <div className="sm:w-44 shrink-0">
                <p className="text-sm">{commission.vendor}</p>
              </div>
              <div className="hidden lg:block sm:w-28 shrink-0">
                <p className="text-sm">{commission.feeAmount}</p>
                <p className="text-xs text-muted-foreground">placement fee</p>
              </div>
              <div className="hidden lg:block sm:w-16 shrink-0">
                <p className="text-sm">{commission.splitPct}%</p>
                <p className="text-xs text-muted-foreground">split</p>
              </div>
              <div className="sm:w-28 shrink-0">
                <p className="text-sm font-medium">{commission.payable}</p>
                <p className="text-xs text-muted-foreground">payable</p>
              </div>
              <div className="shrink-0">
                {commission.status === 'pending' && <Badge variant="warning">Pending</Badge>}
                {commission.status === 'approved' && <Badge variant="info">Approved</Badge>}
                {commission.status === 'paid' && <Badge variant="success">Paid</Badge>}
              </div>
            </div>
          )}
        />
      )}

      {/* Sourcing partners tab */}
      {tab === 'partners' && (
        <DataList
          items={vendors}
          getKey={(v) => v.id}
          pageSize={5}
          empty="No sourcing partners yet."
          renderRow={(vendor) => (
            <div className="flex flex-col gap-1 px-5 py-4 sm:flex-row sm:items-center sm:gap-4 hover:bg-muted/40 transition-colors">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{vendor.name}</p>
                <p className="text-xs text-muted-foreground mt-0.5">GSTIN {vendor.gstin}</p>
              </div>
              <div className="shrink-0 text-sm text-muted-foreground">
                Default split {vendor.defaultSplitPct}%
              </div>
            </div>
          )}
        />
      )}
    </DashboardLayout>
  )
}
